import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './HoverCard.css';

interface HoverCardProps {
  title: ReactNode;
  body: ReactNode;
  width?: number;
  children: ReactNode; // the trigger (e.g. an info icon button)
  triggerClassName?: string;
}

// Shared hover-tooltip "manager": one consistent floating panel used by every
// info-icon tooltip (instrument / client login / LP login). Mirrors the legacy
// RiskDashboard*Tooltip managers — fixed-position panel portaled to <body> so it
// escapes table overflow, opens to the right of the anchor (flips left when it
// would overflow), and stays open while the pointer is over the panel with a
// 280ms hide delay.
export function HoverCard({ title, body, width = 480, children, triggerClassName }: HoverCardProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: -9999, top: -9999 });
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);

  const clearTimer = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const scheduleHide = () => {
    clearTimer();
    timer.current = window.setTimeout(() => setOpen(false), 280);
  };
  const openNow = () => {
    clearTimer();
    setOpen(true);
  };

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const gap = 8;
    const rect = anchor.getBoundingClientRect();
    const tt = panel.getBoundingClientRect();
    const ttW = tt.width || width;
    const ttH = tt.height || 400;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.right + gap;
    if (left + ttW > vw - gap) left = rect.left - ttW - gap;
    if (left < gap) left = gap;

    let top = rect.top;
    if (top + ttH > vh - gap) top = vh - ttH - gap;
    if (top < gap) top = gap;

    setPos({ left, top });
  }, [width]);

  // Reposition whenever the panel opens or its content changes size (async load).
  useLayoutEffect(() => {
    if (open) place();
  }, [open, body, place]);

  return (
    <>
      <span ref={anchorRef} className={triggerClassName} onMouseEnter={openNow} onMouseLeave={scheduleHide}>
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fk-hovercard"
            style={{ left: pos.left, top: pos.top, width }}
            onMouseEnter={clearTimer}
            onMouseLeave={scheduleHide}
          >
            <div className="fk-hovercard-header">{title}</div>
            <div className="fk-hovercard-body">{body}</div>
          </div>,
          document.body,
        )}
    </>
  );
}

export function TooltipSection({ label }: { label: string }) {
  return <div className="fs-11 text-muted text-uppercase fw-semibold mb-1">{label}</div>;
}

export function TooltipGrid({ children }: { children: ReactNode }) {
  return <div className="fk-hovercard-grid">{children}</div>;
}

export function TooltipItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="fk-hovercard-item">
      <label>{label}</label>
      <span>{value}</span>
    </div>
  );
}
